<script lang="ts">
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import PickupOrDeliveryOutsideCart from "@retailer/svelte/Shared/PickupOrDeliveryOutsideCart.svelte";
  import type { Product } from "@shared/types/ProductLine";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import PriceWrapper from "@retailer/svelte/Shared/PriceWrapper.svelte";
  import productContainerDetails from "@shared/filters/product_container_details.js";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { ProductListItemEntity } from "@shared/services/event_tracking/entities";
  import ListsCartWidgetContainer from "@retailer/svelte/CartWidget/v2/Containers/ListsCartWidgetContainer.svelte";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";

  import ProductListRowDistributor from "@retailer/svelte/Shared/QuickOrderList/ProductListRowDistributor.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";

  export let gridProduct;
  export let callbacks;
  export let showPrices: boolean = false;

  let Cart = window["angularInjector"].get("Cart");
  let cartProduct = Cart.mergeMatchedCartProductToProduct(gridProduct.product);
  let realProduct: Product;
  let productLine;

  if (cartProduct) {
    realProduct = cartProduct.product;
    productLine = cartProduct.product_line;
  } else {
    productLine = gridProduct.product_line_placeholder.product_line;
  }

  let distributor = cartProduct ? cartProduct.distributor : productLine.distributor;
  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  // auth service
  let authService = getControlStateAuthenticationService();

  $: hideAllDistributorInfo = authService?.shouldHideAllDistributorInfo?.(
    $RetailerUserAuthenticationsStore,
    [distributor?.id],
  );
  $: authStatus = authService?.getRetailerUserAuthenticationStatus?.(
    $RetailerUserAuthenticationsStore,
    [distributor?.id],
  );

  let sizeDetails;

  if (realProduct) {
    sizeDetails = productContainerDetails()(realProduct, null, null, true);
  }

  const { trackMrcEvents } = EventTracking;

  const entities: ProductListItemEntity[] = [
    new ProductListItemEntity({
      product_id: gridProduct.product_id,
      product_line_id: gridProduct.product?.product_line?.id,
      quick_order_list_id: gridProduct.source_list_id,
      quick_order_list_product_id: gridProduct.id,
    }),
  ];
</script>

<TrackingContext {entities} let:trackingContext>
  <div
    data-testid="product-list-grid-item-{gridProduct.id}"
    use:trackMrcEvents={[trackingContext.entities]}
    class="fizz-card-small fizz-justify-start fizz-text-center fizz-stack-12"
    style="align-items: center;"
  >
    <figure class="fizz-full-width">
      <ProductImage product={productLine} className="fizz-margin-auto" height={150} fizz={true} />
      <figcaption>
        <div class="fizz-stack-4">
          <UIRouterLink
            stateName="layout.product_detail"
            params={{ productId: productLine.id }}
            useTrackClick={true}
            targetObjectType="product_list_item_name"
          >
            <strong>{productLine.name}</strong>
          </UIRouterLink>
          {#if sizeDetails}
            <p class="fizz-text-subdued">{sizeDetails}</p>
          {/if}
          {#if cartProduct && !hideAllDistributorInfo}
            {#if !!cartProduct.inventory?.sku}
              <p class="fizz-text-subdued fizz-text-small" data-testid="distributor-sku">
                SKU: {cartProduct.inventory.sku}
              </p>
            {/if}
          {/if}
        </div>
      </figcaption>
    </figure>
    {#if cartProduct && cartProduct.inventory_id && showPrices && !hideAllDistributorInfo}
      <div class="fizz-stack-8">
        <PriceWrapper
          inventoryId={cartProduct.inventory_id}
          productTypeId={productLine.product_type_id}
          showPricePerOunce={false}
        />
      </div>
    {/if}
    {#if cartProduct && !hideAllDistributorInfo}
      <div style="min-width: 250px;">
        <PickupOrDeliveryOutsideCart
          product={cartProduct}
          {Cart}
          distributor={cartProduct?.distributor}
          callback={() => {
            cartProduct = Cart.mergeMatchedCartProductToProduct(gridProduct.product);
          }}
          justify="center"
        />
        {#if !cartProduct?.distributor?.name}
          <ProductListRowDistributor
            distributor={cartProduct.distributor}
            product={realProduct}
            {productLine}
          >
            <button
              slot="cta"
              class="fizz-button fizz-button-primary fizz-full-width"
              data-testid="grid-select-distributor"
              let:selectDistributor
              on:click|preventDefault={selectDistributor}>Select a distributor</button
            >
          </ProductListRowDistributor>
        {:else if cartProduct.inventory_id}
          <ListsCartWidgetContainer
            inventoryId={cartProduct.inventory_id}
            distributor={cartProduct.distributor}
            productId={gridProduct.product.product_id}
            productLineId={productLine.id}
            {productLine}
            variant="grid"
          />
        {:else}
          <div class="fizz-text-error fizz-flex-row-4">
            <Icon type="Info" />
            <span>
              This size is unavailable. <a href="/products/{productLine.id}">See other sizes</a>
            </span>
          </div>
        {/if}
      </div>
    {:else if hideAllDistributorInfo}
      <div class="fizz-flow-8 fizz-inset-horizontal-32">
        <span class="fizz-flex-row-4 fizz-align-content-start">
          {#if authStatus == "pending"}
            <span style="position: relative; top: 2px;">
              <Icon type="InfoYellow" width="16" height="16" />
            </span>
            <p class="fizz-text-left">{authService?.pendingDescription}</p>
          {:else if authStatus == "invalid"}
            <span style="position: relative; top: 2px;">
              <Icon type="Error" width="16" height="16" />
            </span>
            <p class="fizz-text-left fizz-text-negative">{authService?.invalidDescription}</p>
          {/if}
        </span>
      </div>
    {:else}
      <div>
        <p class="fizz-text-subdued"><em>Size not selected</em></p>
        <a
          href="/"
          on:click|preventDefault={() => {
            callbacks.onChooseSizeForProductLinePlaceholder(productLine);
          }}
        >
          {productLine.distributor_info?.is_vintage_product
            ? "Select a vintage and size"
            : "Select a size"}
        </a>
      </div>
    {/if}
  </div>
</TrackingContext>
